import type { Ref } from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import { cx } from '../lib/cx';
import { mergeRefs } from '../lib/merge-refs';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'defaultValue'> {
  value: string;
}

export function Textarea(
  { ref, className, value, rows, ...rest }: TextareaProps & { ref?: Ref<HTMLTextAreaElement> },
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const fit = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    const scroller = el.closest('.form');
    const scrollTop = scroller?.scrollTop ?? 0;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    if (scroller) scroller.scrollTop = scrollTop;
  }, []);
  useLayoutEffect(fit, [fit, value, rows]);
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    let lastWidth = el.offsetWidth;
    const ro = new ResizeObserver(() => {
      if (el.offsetWidth === lastWidth) return;
      lastWidth = el.offsetWidth;
      fit();
    });
    ro.observe(el);
    let unmounted = false;
    document.fonts?.ready.then(() => {
      if (!unmounted) fit();
    });
    return () => {
      unmounted = true;
      ro.disconnect();
    };
  }, [fit]);
  return (
    <textarea
      ref={mergeRefs(innerRef, ref)}
      className={cx('text', className)}
      value={value}
      rows={rows}
      {...rest}
    />
  );
}
